/** @format */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles, MenuItem, Select } from '@material-ui/core';
import I18n from '../../../components/Translate/I18n';
import Card from '../../../components/Card/Card';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import CardBody from '../../../components/Card/CardBody';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { useDispatch } from 'react-redux';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { LoadPlatformResellersType, PlatformResellerLoader } from '../../../modules/platformreseller/PlatformResellerLoader';
import { PlatformResellerCollectionInterface } from '../../../interfaces/collections/PlatformResellerCollectionInterface';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import DatePickerForm from '../../../components/Form/DatePickerForm';
import GridItem from '../../../components/Grid/GridItem';
import GridContainer from '../../../components/Grid/GridContainer';
import moment from 'moment';
import { ReactComponent as Cancel } from '../../../assets/themes/OPL/img/icons/Cancel.svg';

export type ARReportAddDialogProps = {
    openView: boolean;
    fullWidth: boolean;
    title?: string;
    closeCallback: () => void;
    saveCallback: (params: any) => void;
};

export const ARReportAddDialog = (props: ARReportAddDialogProps): React.ReactElement => {
    const dispatch = useDispatch();
    const useStyle = makeStyles(adescomStyle);
    const classes = useStyle();
    const itemLoader: ItemLoaders = useMemo(() => {
        return new ItemLoaders(dispatch);
    }, [dispatch]);
    const platformResellersLoader: PlatformResellerLoader = useMemo(() => {
        return new PlatformResellerLoader(itemLoader);
    }, [itemLoader]);
    const [resellers, setResellers] = useState<PlatformResellerCollectionInterface | null>(null);
    const [selectedReseller, setSelectedReseller] = useState<number | null>(null);
    const [selectedReportType, setSelectedReportType] = useState<number | null>(null);
    const [selectedCorrectionType, setSelectedCorrectionType] = useState<number | null>(null);
    const [dateStart, setDateStart] = useState<any | null>(null);
    const [dateEnd, setDateEnd] = useState<any | null>(null);

    const resellersCallback = useCallback((): void => {
        setResellers(platformResellersLoader.getPlatformResellerCollection());
    }, [setResellers, platformResellersLoader]);

    useEffect((): void => {
        const params: LoadPlatformResellersType = {
            callback: resellersCallback,
            filters: [],
        };
        platformResellersLoader.loadPlatformResellers(params);
    }, [platformResellersLoader, resellersCallback]);

    const selectReseller = (event: React.ChangeEvent<{ value: unknown }>): void => {
        setSelectedReseller(event.target.value as number);
    };

    const selectReportType = (event: React.ChangeEvent<{ value: unknown }>): void => {
        setSelectedReportType(event.target.value as number);
    };

    const selectCorrectionType = (event: React.ChangeEvent<{ value: unknown }>): void => {
        setSelectedCorrectionType(event.target.value as number);
    };

    const saveCallback = (): void => {
        const params: { type?: string; params?: any } = {};
        if (!selectedReportType) {
            return;
        }
        let type = '';
        if (selectedReportType === 3) {
            type = 'report.pivot';
        }
        if (selectedReportType === 4) {
            type = 'report.ar';
        }
        if (selectedReportType === 6) {
            type = 'report.iaf';
        }
        if (selectedReportType === 8) {
            type = 'report.ap';
        }

        if (selectedReportType !== 3 && (!dateStart || !dateEnd)) {
            return;
        }

        params.type = type;
        params.params = JSON.stringify({
            type: selectedReportType,
            includePrevious: true,
            dateStart: dateStart ? moment(dateStart).format('YYYY-MM-DD') : null,
            dateEnd: dateEnd ? moment(dateEnd).format('YYYY-MM-DD') : null,
        });

        props.saveCallback(params);
    };

    const changeDateStart = (moment: any): void => {
        setDateStart(moment);
    };

    const changeDateEnd = (moment: any): void => {
        setDateEnd(moment);
    };

    return (
        <Dialog
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
            keepMounted
            open={props.openView}
            fullWidth={props.fullWidth}
            maxWidth="sm"
        >
            <DialogTitle>
                <GridContainer justifyContent={'space-between'}>
                    <h4>
                        {props.title === null && ''}
                        {typeof props.title === 'string' ? <I18n trans={props.title} /> : props.title}
                    </h4>
                    <Button onClick={props.closeCallback} color="primary">
                        <Cancel width={24} height={24} />
                    </Button>
                </GridContainer>
            </DialogTitle>
            <DialogContent>
                <Card>
                    <CardHeader trans="documents:report.report_type" />
                    <CardBody>
                        <Select
                            value={selectedReportType}
                            onChange={selectReportType}
                            style={{ minWidth: '300px' }}
                            MenuProps={{
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left',
                                },
                                elevation: 0,
                                getContentAnchorEl: null,
                                className: classes.selectMenu,
                            }}
                        >
                            <MenuItem key={'report_type_1'} value={3}>
                                <I18n trans="documents:report.type.3" />
                            </MenuItem>
                            <MenuItem key={'report_type_1'} value={4}>
                                <I18n trans="documents:report.type.4" />
                            </MenuItem>
                            <MenuItem key={'report_type_2'} value={6}>
                                <I18n trans="documents:report.type.6" />
                            </MenuItem>
                            <MenuItem key={'report_type_2'} value={8}>
                                <I18n trans="documents:report.type.8" />
                            </MenuItem>
                        </Select>
                    </CardBody>
                    {selectedReportType !== 3 && (
                        <div style={{ marginTop: 15 }}>
                            <CardHeader trans="documents:report.period" />
                            <CardBody style={{ minHeight: '40vh' }}>
                                <GridContainer spacing={2}>
                                    <GridItem xs={6}>
                                        <DatePickerForm callback={changeDateStart} dateFormat={'YYYY-MM-DD'} timeFormat={false} />
                                    </GridItem>
                                    <GridItem xs={6}>
                                        <DatePickerForm callback={changeDateEnd} dateFormat={'YYYY-MM-DD'} timeFormat={false} />
                                    </GridItem>
                                </GridContainer>
                            </CardBody>
                        </div>
                    )}
                </Card>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom callback={saveCallback} label="general:common.save" />
            </DialogActions>
        </Dialog>
    );
};

export default ARReportAddDialog;
